import {AfterViewInit, Component, OnInit, Optional, ViewContainerRef, Inject, Renderer2, inject} from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from "@angular/router";
import { DOCUMENT } from '@angular/common';
import { AbstractTracker, EventName } from '@signature-it/ngx-generic/classes';
import {
    AuthService,
    LanguageService,
    GoogleAnalyticsService,
    FacebookAnalyticsService,
    FeaturesService,
    EventBusService,
    SeoService_Generic,
    GlobalLinkService
} from '@signature-it/ngx-generic/services';
import { ILanguage, IGoogleAnalyticsConfig, IFacebookAnalyticsConfig } from '@signature-it/ngx-generic/interfaces';
import { GenericConfig } from '@signature-it/ngx-generic/config';

declare let _: any;
declare let $: any;
declare let localStorage: any;
declare const window: any;


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
    globalLinkSrv = inject(GlobalLinkService); // Don't remove it, It's for multi languages.
    protected viewRef: ViewContainerRef;
    protected googleAnalyticsConfig: IGoogleAnalyticsConfig;
    protected facebookAnalyticsConfig: IFacebookAnalyticsConfig;
    layout: string = 'default-sidebar-1'; //default-sidebar-1,off-canvas-1,sidebar-over-1,off-canvas-1,top-navigation-1,top-navigation-2,empty-view-1
    background: string = 'light'; //light,dark,indigo,blue-grey
    navbar: string = 'light'; //light,dark,indigo,blue-grey
    sidebar: string = 'light'; //light,dark,indigo,blue-grey
    topNavigation: string = 'light'; //light,dark,indigo,blue-grey
    collapsed: boolean = true; //true,false
    controller: string;
    view: string;
    currentLangInitialized = false;
    prevUrl: string;

    constructor(protected router: Router,
                protected auth: AuthService,
                protected analyticsSvc: GoogleAnalyticsService,
                protected langSvc: LanguageService,
                protected featureSvc: FeaturesService,
                viewRef: ViewContainerRef,
                protected renderer2: Renderer2,
                @Inject(DOCUMENT) protected doc,
                protected eventBusService: EventBusService,
                protected seoSvc: SeoService_Generic,
                protected facebookSvc: FacebookAnalyticsService) {

        this.seoSvc.setIndexFollow();

        // small hack in order to catch application root view container ref
        this.viewRef = viewRef;
        // init user's data
        auth.handleAuthentication();

        this.langSvc.initLangList();

        this.langSvc
            .languageSelected$
            .subscribe((lang: ILanguage) => {
                if(lang) {
                    // create Google Analytics scripts
                    this.analyticsSvc.create(this.googleAnalyticsConfig = {
                        tracking_id: lang.ga_account
                    });
                    this.facebookSvc.create(this.facebookAnalyticsConfig = {
                        tracking_id: lang.facebook_id
                    });
                    this.seoSvc.setOgTag('locale', lang.og_locale);
                    this.seoSvc.setOgTag('site_name', lang.og_site_name);
                }
            });

        this.eventBusService.on(EventName.DATA_FOR_ANALYTICS_READY, ({page, data}) => {
            window.dataLayer = window.dataLayer || [];
            let createGTM = !window.dataLayer.length;
            this.pushToDataLayer(page, data);
            if (createGTM && GenericConfig.GTM_ID) {
                this.analyticsSvc.createGTM(this.googleAnalyticsConfig = {
                    tracking_id: GenericConfig.GTM_ID
                });
            }
        });

        this.eventBusService.on(EventName.DICTIONARY_REQUEST_COMPLETED, completed => {
            this.currentLangInitialized = completed;
        });
    }

    ngOnInit(): void {
        // layout attributes
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationStart) {
              this.handleNavigationStart(val)
            } else if (val instanceof NavigationEnd) {
                this.handleNavigationEnd();
            } else {
                return;
            }
        });
    }

    ngAfterViewInit() {
        if(GenericConfig.favicon) {
            document.querySelector("[rel='icon']").setAttribute('href', GenericConfig.favicon);
        }
        // small hack in order to late loading
        setTimeout(() => {
            $('[data-toggle="tooltip"]').tooltip();
        }, 1000);
        this._injectEntityGenericScriptsAndStyles();
        this._injectExternalResources();
    }


    protected _setBodyAttributes(attributes) {
        $('body').attr('data-layout', attributes['layout']);
        $('body').attr('data-controller', attributes['controller']);
        $('body').attr('data-background', attributes['background']);
        $('body').attr('data-sidebar', attributes['sidebar']);
        $('body').attr('data-navbar', attributes['navbar']);
        $('body').attr('data-top-navigation', attributes['topNavigation']);
        $('body').attr('data-view', attributes['view']);
        $('body').attr('data-collapsed', attributes['collapsed']);
    }

    /**
     * send pageviews analytics
     */
    protected _registerAnalyticsSendPageView(): void {
        this._getAnalyticsInstances().forEach((t: AbstractTracker) => {
            t.sendPageView(this.router.url);
        });
    }

    protected _getAnalyticsInstances(): Array<AbstractTracker> {
        return [this.analyticsSvc];
    }

    protected _injectExternalResources(): void {
        this.featureSvc.cookieDisclaimer$.subscribe(res => {
            if(!res) return;
            if (GenericConfig.injectExternalResources && !location.pathname.startsWith('/admin')) {
                const isBot = navigator.userAgent.match(/Prerender|googlebot|whatsapp|baiduspider|facebookexternalhit|rogerbot|twitterbot|linkedinbot|embedly|quora\\ link\\ preview|showyoubot|outbrain|pinterest|slackbot|vkShare|W3C_Validator/i) !== null;
                this.featureSvc.getExternalResources().subscribe(resources => {
                    _.each(resources, (resourceList, location) => {
                        const parentElement = this.doc.getElementsByTagName(location)[0];
                        if (parentElement) {
                            _.each(resourceList, (resource) => {
                                const hasPages = resource['attributes']['pages'] != null;
                                if (!hasPages || (resource['attributes']['pages'].split("\n").indexOf(window.location.pathname) >= 0 )){
                                    if (!(resource.ignore_bot && isBot)) {
                                        const element = this.renderer2.createElement(resource.element);
                                        if (resource.attributes.text) {
                                            $(element).append(resource.attributes.text);
                                            delete resource.attributes.text;
                                        }
                                        _.each(resource.attributes, (val, attr) => {
                                            element.setAttribute(attr, val);
                                        });
                                        this.renderer2.appendChild(parentElement, element);
                                    }
                                }
                            });
                        }
                    });
                });
            }
        });
    }

    protected _injectEntityGenericScriptsAndStyles(): void {
        this.featureSvc.getEntityGenericScriptsAndStyles().subscribe(res => {
            const head = this.doc.getElementsByTagName('head')[0];
            let jsElement = this.renderer2.createElement('script');
            jsElement.text =  res['js'];
            jsElement.setAttribute('async', '');
            jsElement.setAttribute('type', 'text/javascript');
            this.renderer2.appendChild(head, jsElement);
            let styleElement = this.renderer2.createElement('style');
            styleElement.innerHTML =  res['css'];
            styleElement.setAttribute('type', 'text/css');
            this.renderer2.appendChild(head, styleElement);
        });
    }

    protected handleNavigationStart(val) {
        const copy = _.assign({}, val);
        const url = copy.url;

        this.setRedirect(url);
        this.setCollapsed(url);
        this.setConfig();
        if(!this.prevUrl ||  this.prevUrl != val.url.split('?')[0]) {
            this.prevUrl = val.url.split('?')[0];
            this.scrollTop();
        }
    }

    protected handleNavigationEnd() {
		// send Google Analytics pageviews
		this.analyticsSvc.sendPageView(this.router.url);
		this.facebookSvc.sendPageView('');
		this.seoSvc.setCanonical();
		this.seoSvc.setHreflang();
		this.seoSvc.setOgTag('url', location.origin + location.pathname);
    }
    
    protected setRedirect(url) {
        const self = this;

        if(url == '/' && GenericConfig.redirectRootUrl) {
            if(/^https?:\/\//i.test(GenericConfig.redirectRootUrl)) {
                window.location.href = GenericConfig.redirectRootUrl;
            } else {
                this.router.navigate([GenericConfig.redirectRootUrl]);
            }
        }
    }

    protected setCollapsed(url) {
        const self = this;
        //set body `data-*` attributes
        self.collapsed = true;
        const data = url.split('/').filter(url => url.length > 0);
        switch(data[0]) {
            case 'account':
                self.layout = 'account';
                self.controller = self.layout;
                self.collapsed = false;
                break;
            default:
                self.layout = 'main';
                self.controller = (!_.isEmpty(data[0])) ? data[0] : 'homepage';
                self.collapsed = true;
                break;

        }
        self.view = (!_.isEmpty(data[1])) ? data[1] : self.controller;
    }

    protected setConfig() {
        const self = this;
        // config attributes
        let config = {};
        config = _.assign({},
            {
                layout: this.layout,
                background: this.background,
                navbar: this.navbar,
                sidebar: this.sidebar,
                topNavigation: this.topNavigation,
                collapsed: this.collapsed,
                preferedLangCode: 'en',
                langCode: '*'
            }
        );
        config['view'] = self.view;
        config['layout'] = self.layout;
        config['background'] = self.background;
        config['navbar'] = self.navbar;
        config['sidebar'] = self.sidebar;
        config['topNavigation'] = self.topNavigation;
        config['collapsed'] = self.collapsed;
        config['controller'] = self.controller;
        config['collapsed'] = self.collapsed;
        this._setBodyAttributes(config);
    }

    scrollTop() {
        window.scrollTo(0, 0);
    }

    pushToDataLayer(page, data) {}
}
